import { readUwData, readUwEntryData, toNumberOrNull } from '../compliancesBefe/mappers';
import type { UwServiceEntry } from '../compliancesBefe/types';
import { getFiscalYearSuffix } from '../../../utils/dateUtils';
import { inrAmount } from '../../../utils/formatters/inrAmount';
import { formatLacCr } from '../../../utils/formatters/formatLacCr';
import type { BankTransaction } from '../../../modules/Dashboard/DashboardModules/Financials/components/FinancialsFromBankingV2/types';
import type {
    RptBankTxnResponse,
    RptParty,
    RptPartyRow,
    RptTolerance,
    UwRelatedPartyTransactionsData,
} from './relatedPartyTransactions.types';

export const UW_FETCH_RELATED_PARTY_TRANSACTIONS = 'UW_FETCH_RELATED_PARTY_TRANSACTIONS';
const UW_FETCH_RELATED_PARTY_BANK_TRANSACTIONS = 'UW_FETCH_RELATED_PARTY_BANK_TRANSACTIONS';

const toTolerance = (value: Partial<RptTolerance> | null | undefined): RptTolerance | null => {
    const score = toNumberOrNull(value?.score);
    const deviationPct = toNumberOrNull(value?.deviationPct);
    if (score === null || deviationPct === null || !value?.status) return null;
    return { score, deviationPct, status: value.status };
};

const toPartyRow = (row: Partial<RptPartyRow>): RptPartyRow => ({
    financialYear: row.financialYear ?? '',
    transactionType: row.transactionType ?? '',
    mca: toNumberOrNull(row.mca),
    mcaRaw: row.mcaRaw ?? null,
    gst: toNumberOrNull(row.gst),
    banking: toNumberOrNull(row.banking),
    mcaTolerance: toTolerance(row.mcaTolerance),
    gstTolerance: toTolerance(row.gstTolerance),
});

/** The RPT parties from the related-parties response's RPT service entry; null when that service failed or is absent. */
export function toRptParties(serviceEntry: unknown): RptParty[] | null {
    const data = readUwEntryData(serviceEntry as UwServiceEntry<UwRelatedPartyTransactionsData> | null | undefined);
    if (!data) return null;
    return (data.parties ?? [])
        .filter((party) => !!party?.partyKey)
        .map((party) => ({
            partyKey: party.partyKey as string,
            partyName: party.partyName ?? '',
            bucket: party.bucket ?? '',
            transactionCount: toNumberOrNull(party.transactionCount) ?? 0,
            singleAmount: toNumberOrNull(party.singleAmount),
            attachedFromKids: party.attachedFromKids ?? [],
            isOtherKmp: !!party.isOtherKmp,
            rows: (party.rows ?? []).map(toPartyRow),
            partyKid: party.partyKid ?? null,
            relationship: party.relationship ?? null,
            aicaStatus: party.aicaStatus,
            entityDetails: party.entityDetails,
        }));
}

export function toRptBankTransactions(body: unknown): BankTransaction[] | null {
    const data = readUwData<RptBankTxnResponse>(body, UW_FETCH_RELATED_PARTY_BANK_TRANSACTIONS);
    if (!data) return null;
    return (data.transactions ?? []).map((txn) => ({
        id: Number(txn.id),
        date: txn.date,
        amount: txn.amount,
        transactionType: txn.transactionType,
        transactionNote: txn.transactionNote ?? '',
        taggedEntities: txn.taggedEntities ?? [],
    }));
}

/** FY label from its end year: 2025 → "FY'24-25"; null when not a year. */
export const formatFyFromEndYear = (endYear: unknown): string | null => {
    const year = toNumberOrNull(endYear);
    return year === null || year <= 0 ? null : `FY'${getFiscalYearSuffix(year - 1)}`;
};

/** "2025-03-31" (FY end date) → "FY'24-25". */
export const formatFy = (fyEndDate: string): string => formatFyFromEndYear(fyEndDate.slice(0, 4)) ?? fyEndDate;

/** Lac/Cr short form that keeps the sign: -10000000 → "-1.00 Cr". */
export const formatRptAmount = (amount: number): string => (amount < 0 ? `-${formatLacCr(-amount)}` : formatLacCr(amount));

/** Full en-IN rupees with a leading minus: -290415 → "-2,90,415". */
export const formatDrawerAmount = (amount: number): string => {
    const formatted = inrAmount(Math.abs(amount));
    return amount < 0 ? `-${formatted}` : formatted;
};

/** fromKid → the party whose Transactions badge sits on that row; the first party in response order wins. */
export function buildPartyByFromKid(parties: RptParty[] | null | undefined): Map<string, RptParty> {
    const byFromKid = new Map<string, RptParty>();
    (parties ?? []).forEach((party) =>
        party.attachedFromKids.forEach((fromKid) => {
            if (!byFromKid.has(fromKid)) byFromKid.set(fromKid, party);
        }),
    );
    return byFromKid;
}
